import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import BuildIdEvent, { BuildIdEventProcessing } from "./BuildIdEvent";

const SignalREvent = {
  ConnectionStatus: "SignalREvents:ConnectionStatus"
};

const SignalRMethod = {
  SEND_MESSAGE_TO_CHAT_GROUP: "SendMessageToChatGroup",
  CHANGE_CURRENT_ACTIVE_GROUP: "ChangeCurrentActiveGroup",
  SEND_MESSAGE_TO_CLIENT_CHAT_GROUP: "SendMessageToClientChatGroup",
  CHANGE_CURRENT_ACTIVE_CLIENT_CHAT_GROUP: "ChangeCurrentActiveClientGroup",
  REPORTED_CHAT_GROUP: "ReportedChatGroup",
  DELETE_CHAT_GROUP: "DeleteChatGroup"
};

const SignalRListener = {
  GLOBAL_LISTENER: "GlobalListener"
};

let Hub = undefined;
let ConnectionStatus = HubConnectionState.Disconnected;
const Url = import.meta.env.REACT_APP_BASE_URL;
const signalRConnectionStatusCallbacks = new Map();

const SignalR = {
  connect: (loginData) =>
    new Promise((resolve, reject) => {
      const process = async () => {
        const ConnectionString = await GetConnectionString(loginData);

        if (!ConnectionString) {
          console.error("connect-> Error As SignalR Connection String Is Undefined.");
          return reject(new Error("SignalR Connection String Is Undefined"));
        }
        try {
          Hub = new HubConnectionBuilder()
            .withUrl(ConnectionString)
            .withAutomaticReconnect({
              nextRetryDelayInMilliseconds: (retryContext) => {
                if (retryContext.previousRetryCount < 10) {
                  return Math.min(retryContext.previousRetryCount * 2000, 30000);
                }
                return null;
              }
            })
            .build();
          if (Hub instanceof HubConnection) {
            await Hub.start().then(ConnectionSuccessful).catch(ConnectionFailed);
            Hub.onreconnected(onConnectionReconnected);
            Hub.onclose(onConnectionClosed);
          }
        } catch (err) {
          console.error("connect-> Error While Making Connection Of SignarR: ", err);
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      };

      const ConnectionSuccessful = () => {
        if (Hub.state !== ConnectionStatus) {
          ConnectionStatus = Hub.state;
          Hub.on(SignalRListener.GLOBAL_LISTENER, IncomingEvent);
          SignalREvents(SignalREvent.ConnectionStatus, ConnectionStatus);
          resolve(ConnectionStatus);
        }
      };

      const ConnectionFailed = (error) => {
        try {
          const newState = Hub?.state || HubConnectionState.Disconnected;
          if (ConnectionStatus !== newState) {
            ConnectionStatus = newState;
            SignalREvents(SignalREvent.ConnectionStatus, ConnectionStatus);
          }
          reject(error instanceof Error ? error : new Error(String(error || ConnectionStatus)));
        } catch (err) {
          console.log("ConnectionFailed-> Error While Handling Connection Failure: ", err);
          reject(new Error("Connection Failed"));
        }
      };

      const onConnectionClosed = (error) => {
        ConnectionStatus = Hub.state;
        SignalREvents(SignalREvent.ConnectionStatus, ConnectionStatus);
      };

      const onConnectionReconnected = (connectionId) => {
        ConnectionStatus = Hub.state;
        SignalREvents(SignalREvent.ConnectionStatus, ConnectionStatus);
      };

      if (
        Hub instanceof HubConnection &&
        (Hub.state === HubConnectionState.Connected || Hub.state === HubConnectionState.Connecting)
      ) {
        ConnectionSuccessful();
      } else {
        process();
      }
    }),

  disconnect: async () => {
    if (Hub instanceof HubConnection) {
      try {
        if (Hub.state === HubConnectionState.Connected) {
          await SignalR.onConnectionClosing();
        }
      } catch (error) {
        console.log("disconnect-> Error While Closing Connection: ", error);
      } finally {
        try {
          await Hub.stop();
        } catch (stopError) {
          console.log("disconnect-> Error Stopping Hub: ", stopError);
        }
      }
    }
  },

  invoke: (methodName, args) =>
    new Promise((resolve, reject) => {
      switch (methodName) {
        case SignalRMethod.SEND_MESSAGE_TO_CHAT_GROUP:
          {
            Hub.invoke(SignalRMethod.SEND_MESSAGE_TO_CHAT_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        case SignalRMethod.CHANGE_CURRENT_ACTIVE_GROUP:
          {
            Hub.invoke(SignalRMethod.CHANGE_CURRENT_ACTIVE_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        case SignalRMethod.SEND_MESSAGE_TO_CLIENT_CHAT_GROUP:
          {
            Hub.invoke(SignalRMethod.SEND_MESSAGE_TO_CLIENT_CHAT_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        case SignalRMethod.CHANGE_CURRENT_ACTIVE_CLIENT_CHAT_GROUP:
          {
            Hub.invoke(SignalRMethod.CHANGE_CURRENT_ACTIVE_CLIENT_CHAT_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        case SignalRMethod.REPORTED_CHAT_GROUP:
          {
            Hub.invoke(SignalRMethod.REPORTED_CHAT_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        case SignalRMethod.DELETE_CHAT_GROUP:
          {
            Hub.invoke(SignalRMethod.DELETE_CHAT_GROUP, ...args)
              .then(resolve)
              .catch(reject);
          }
          break;
        default:
      }
    }),

  onConnectionClosing: () =>
    new Promise((resolve, reject) => {
      BuildIdEvent.sendChatGroupDetails(0, false).then(resolve).catch(reject);
      BuildIdEvent.sendChatGroupDetails(0, true).then(resolve).catch(reject);
      resolve();
    }),

  connectionStatusListener: (callback, routeKey) => {
    if (!routeKey) {
      console.error("connectionStatusListener-> Route key is required for unique constraint.");
      return;
    }
    if (signalRConnectionStatusCallbacks.has(routeKey)) {
      // console.log(
      //   `connectionStatusListener-> Callback for routeKey "${routeKey}" already exists. Replacing it.`
      // );
    }
    signalRConnectionStatusCallbacks.set(routeKey, callback);
    SignalREvents(SignalREvent.ConnectionStatus, ConnectionStatus);
  },

  removeListener: (key) => {
    signalRConnectionStatusCallbacks.delete(key);
  },

  getState: () => ConnectionStatus,
  getConnectionId: () => Hub?.connectionId
};

function SignalREvents(type, data) {
  if (type === SignalREvent.ConnectionStatus) {
    signalRConnectionStatusCallbacks.forEach((callbackFunc) => callbackFunc(data));
  }
}

function IncomingEvent(event) {
  if (!Array.isArray(event)) {
    event = [event];
  }
  event.forEach((item) => {
    const { key, data } = item;
    BuildIdEventProcessing(key, data);
  });
}
async function GetConnectionString(loginData) {
  try {
    const userId = loginData?.userInformation?.UserId;
    const deviceId = loginData?.userInformation?.DeviceId;

    const environment =
      Url === "https://bimiscwebapi-test.azurewebsites.net/api"
        ? // ?
          "-test"
        : // "development"
          Url === "https://bimiscwebapi.azurewebsites.net/api"
          ? ""
          : "-staging";

    // return `https://bimiscwebapi-signalr-${environment}.azurewebsites.net/global?username=${userId}&deviceid=${deviceId}`;
    return `https://bimiscwebapi${environment}.azurewebsites.net/signalr-hubs/global?username=${userId}&deviceid=${deviceId}`;
  } catch (err) {
    console.error("GetConnectionString-> SignalR.GetConnectionString: ", err);
    return null;
  }
}
export { SignalRMethod };
export default SignalR;
